import Grid from "@mui/material/Grid2";
import {
  Autocomplete,
  Button,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import ProcessLogsPieChart from "../ProcessLogsPieChart/ProcessLogsPieChart";
import DcProcessFilterCard from "../ProcessLogsFilterCard/DcProcessLogsFilterCard";
import DcProcessLogTable from "../ProcessLogsTable/DcProcessLogsTable";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import {
  selectUserToken,
  setDisplayErrorSnackBar,
} from "../../../../../features/userSlice";
import { isGreaterEndDate } from "../../../../../utils/helpers";
import { useMonitoringProcessLogs } from "../../../../../customHooks/useMonitoringProcessLogs";
import { Search as SearchIcon } from "@mui/icons-material";
import { useEffect, useState } from "react";
import {
  setIsProcessLogsLoading,
  setPage,
  setSelectedPackageAutoComplete,
  setSelectedProcessLogsDateRange,
} from "../../../../../features/monitoringProcessLogsSlice";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DateRangePicker } from "@mui/x-date-pickers-pro";
import { SingleInputDateRangeField } from "@mui/x-date-pickers-pro/SingleInputDateRangeField";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import dayjs from "dayjs";
import "dayjs/locale/en-gb"; // Import the locale for British English
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Stack } from "@mui/system";
import { useSearchParams } from "react-router-dom";
import { ProcessLogsEndPointLogic } from "../../../../../logic/DataCollection/Monitoring/ProcessLogsEndPointLogic";
import PackageSkeleton from "../../Dc_Packages/PackagesBase/PackageSkeleton";

const tableHeadings = {
  run_id: "Run ID",
  workflow_name: "Workflow Name",
  package_name: "Package",
  process_name: "Process Name",
  pod_name: "Pod Name",
  start_time: "Start Time",
  end_time: "End Time",
  status: "Status",
  brand: "Brand",
  interface: "Interface",
  run_method: "Run Method",
  step: "Step",
  more_info: "More Info",
};
const DcProcessLogsBase = () => {
  const dispatch = useDispatch();
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const [searchParams, setSearchParams] = useSearchParams();
  const [backendSortLoading, setBackendSortLoading] = useState(false);
  const { isProcessLogsLoading, selectedProcessLogsDateRange } = useSelector(
    (state: any) => state.monitoringProcessLogs
  );
  const {
    selectedLogsPackage,
    selectedLogsProcessName,
    selectedLogsStatus,
    selectedLogsBrand,
    selectedLogsInterface,
    selectedRunMethodList,
    selectedStepList,
    selectedWorkFlow,
    processLogsList,
    selectedPackageAutoComplete,
    isTableLoading,
  } = useSelector((state: any) => state.monitoringProcessLogs);
  const getCurrentFilters = () => ({
    package_name: selectedLogsPackage?.length ? selectedLogsPackage : undefined,
    process_id: selectedLogsProcessName?.length
      ? selectedLogsProcessName
      : undefined, // Changed from process_name
    status: selectedLogsStatus?.length ? selectedLogsStatus : undefined,
    brand_id: selectedLogsBrand?.length ? selectedLogsBrand : undefined,
    interface_id: selectedLogsInterface?.length
      ? selectedLogsInterface
      : undefined, // Added _id suffix for consistency
    run_method: selectedRunMethodList?.length
      ? selectedRunMethodList
      : undefined,
    step: selectedStepList?.length ? selectedStepList : undefined,
    workflow_name: selectedWorkFlow?.length
      ? selectedWorkFlow
      : searchParams.get("workflow_name") || undefined,
  });

  const filters = getCurrentFilters();

  // -- Get all process logs(Custom Hook)
  const { fetchMonitoringProcessLogs } = useMonitoringProcessLogs();

  //-- Get all process logs
  useEffect(() => {
    if (!oauthTokenHeader) return;

    const paramStart = searchParams.get("start_date");
    const paramEnd = searchParams.get("end_date");
    const paramPackage = searchParams.get("package_name");
    const filters = getCurrentFilters();
    const today = dayjs().format("YYYY-MM-DD");
    const startDate = paramStart ? dayjs(paramStart) : dayjs(today);
    const endDate = paramEnd ? dayjs(paramEnd) : dayjs(today);
    if (paramPackage) {
      dispatch(setSelectedPackageAutoComplete(paramPackage));
    }

    dispatch(setSelectedProcessLogsDateRange([startDate, endDate]));
    const effectiveFilters = {
      ...filters,
      package_name: paramPackage ? [paramPackage] : filters.package_name,
    };
    fetchMonitoringProcessLogs(
      startDate.format("YYYY-MM-DD"),
      endDate.format("YYYY-MM-DD"),
      undefined,
      undefined,
      effectiveFilters
    );

    // If no params in URL, set them
    if (!paramStart || !paramEnd) {
      const newParams: any = {
        start_date: startDate.format("YYYY-MM-DD"),
        end_date: endDate.format("YYYY-MM-DD"),
      };
      if (paramPackage) {
        // preserve package_name in URL if it existed
        newParams.package_name = paramPackage;
      }
      setSearchParams(newParams);
    }

    // eslint-disable-next-line
  }, []);

  // -- Get Process Logs by Selected Date Range
  // -- Get Process Logs by Selected Date Range
  const getProcessLogs = () => {
    const [startDate, endDate] = selectedProcessLogsDateRange;
    const updatedFilters = {
      ...filters,
      package_name: selectedPackageAutoComplete
        ? [selectedPackageAutoComplete]
        : undefined,
    };

    // Reset Redux page
    dispatch(setPage(0));

    // Build URLSearchParams and include package_name when present
    const updatedParams = new URLSearchParams(window.location.search);
    updatedParams.set("page", "0");
    updatedParams.set("start_date", startDate.format("YYYY-MM-DD"));
    updatedParams.set("end_date", endDate.format("YYYY-MM-DD"));

    if (selectedPackageAutoComplete) {
      // we store raw package string in URL; setSearchParams will encode as needed
      updatedParams.set("package_name", selectedPackageAutoComplete);
    } else {
      // remove package_name if no package selected
      updatedParams.delete("package_name");
    }

    // Apply URL change so refresh preserves the chosen package/date/page
    setSearchParams(updatedParams);

    // Finally call the API with filters including package_name if present
    fetchMonitoringProcessLogs(
      startDate.format("YYYY-MM-DD"),
      endDate.format("YYYY-MM-DD"),
      undefined,
      undefined,
      updatedFilters
    );
  };

  useEffect(() => {
    try {
      dispatch(setIsProcessLogsLoading(true));
      ProcessLogsEndPointLogic.getAllFilters(
        oauthTokenHeader,
        [
          "status",
          "BRAND",
          // "workflow_name",
          "process_name",
          "interface",
          // "package",
          "run_method",
          "step",
        ],
        dispatch
      );
      ProcessLogsEndPointLogic.getBigFilters(
        oauthTokenHeader,
        [
          //"package",
          "workflow_name",
        ],
        dispatch
      );
    } catch (error) {
      console.error("Error fetching process logs:", error);
    } finally {
      dispatch(setIsProcessLogsLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Derive unique package_name list from processLogsList
  const packageOptions = Array.from(
    new Set(
      processLogsList?.map((item: any) => item.package_name).filter(Boolean)
    )
  );

  return (
    <>
      <Paper elevation={0} sx={{ padding: "8px 16px" }}>
        <Grid container alignItems="center" columnSpacing={2}>
          <Grid size={4}>
            <Typography variant="h3" sx={{ fontSize: "24px" }}>
              Process logs
            </Typography>
            <Typography variant="body2" sx={{ fontSize: "14px" }}>
              Review and monitor Data collection Process Logs.
            </Typography>
          </Grid>
          <Grid size={8}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent={"flex-end"}
              gap={2}
            >
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateRangePicker
                  slots={{ field: SingleInputDateRangeField }}
                  name="allowedRange"
                  slotProps={{
                    textField: {
                      size: "small",
                      color: "secondary",
                      sx: {
                        "& .MuiInputBase-input": {
                          cursor: "pointer",
                        },
                        width: 250,
                      },
                      placeholder: "Select Date Range",
                      InputLabelProps: { shrink: true },
                      label: "Date Range",
                      InputProps: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <CalendarTodayIcon
                              fontSize="small"
                              sx={{ cursor: "pointer" }}
                            />
                          </InputAdornment>
                        ),
                      },
                    },
                  }}
                  format="DD/MM/YYYY"
                  value={selectedProcessLogsDateRange}
                  disabled={
                    isProcessLogsLoading || isTableLoading || backendSortLoading
                  }
                  onChange={(newValue: any) => {
                    const [startDate, endDate] = newValue || [];

                    // Ensure valid values before proceeding
                    if (!startDate || !endDate) return;

                    // Validate if the end date is before the start date
                    if (isGreaterEndDate({ startDate, endDate })) {
                      dispatch(
                        setDisplayErrorSnackBar({
                          message: "Start date cannot be after end date",
                          type: "error",
                        })
                      );
                      return;
                    }

                    dispatch(
                      setSelectedProcessLogsDateRange([startDate, endDate])
                    );
                    setSearchParams({
                      start_date: startDate.format("YYYY-MM-DD"),
                      end_date: endDate.format("YYYY-MM-DD"),
                    });
                  }}
                />
              </LocalizationProvider>
              <Autocomplete
                disablePortal
                disabled={
                  isProcessLogsLoading || isTableLoading || backendSortLoading
                }
                size="small"
                sx={{ width: 380 }}
                value={selectedPackageAutoComplete}
                onChange={(_event, newValue) => {
                  dispatch(setSelectedPackageAutoComplete(newValue || ""));
                  if (!newValue) {
                    dispatch(setPage(0));
                    const updatedParams = new URLSearchParams(
                      window.location.search
                    );
                    updatedParams.set("page", "0");
                    updatedParams.delete("package_name");
                    setSearchParams(updatedParams);

                    const [startDate, endDate] = selectedProcessLogsDateRange;
                    const updatedFilters = {
                      ...filters,
                      package_name: undefined,
                    };

                    fetchMonitoringProcessLogs(
                      startDate.format("YYYY-MM-DD"),
                      endDate.format("YYYY-MM-DD"),
                      undefined,
                      undefined,
                      updatedFilters
                    );
                  }
                }}
                options={packageOptions || []}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Package (optional)"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ marginLeft: "12px" }} />
                        </InputAdornment>
                      ),
                    }}
                    color="secondary"
                  />
                )}
              />
              <Button
                data-tracker="348"
                variant="contained"
                disabled={
                  isProcessLogsLoading || isTableLoading || backendSortLoading
                }
                color="secondary"
                onClick={getProcessLogs}
                sx={{
                  padding: "8px 22px",
                }}
              >
                get data
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>
      <Paper elevation={0} sx={{ p: 2, marginTop: "10px" }}>
        {isProcessLogsLoading || isTableLoading || backendSortLoading ? (
          <PackageSkeleton />
        ) : (
          <>
            <DcProcessFilterCard />
            <ProcessLogsPieChart />
            <DcProcessLogTable
              tableHeadings={tableHeadings}
              backendSortLoading={backendSortLoading}
              setBackendSortLoading={setBackendSortLoading}
            />
          </>
        )}
      </Paper>
    </>
  );
};

export default DcProcessLogsBase;
